/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import * as React from 'react';
import type { Map } from 'mapbox-gl';

export type MapErrorType = 'webgl' | 'map_error';

export interface FitBoundsToMarkersOptions {
    speed?: number;
    maxDuration?: number;
}

/**
 * Value exposed to descendants of `MapProvider`.
 *
 * - `mapInstance` is `null` until the map's style has fully loaded (the
 *   `styledata` event). Hooks that touch sources/layers must guard on this.
 * - `mapErrorType` is set to `'webgl'` when the browser lacks WebGL support
 *   or the map constructor throws, and to `'map_error'` when Mapbox emits an
 *   `error` event after init.
 * - `fitBoundsToMarkers` is provided as a no-op by `MapProvider`. The
 *   `useMapMarkers` hook overrides it via `registerFitBoundsToMarkers` in
 *   Phase 3 so sibling components can trigger a re-fit.
 */
export interface MapContextValue {
    mapInstance: Map | null;
    /**
     * React 18's `MutableRefObject` shape — assignable to a JSX `ref` prop
     * because `.current` is writable. `RefObject<T | null>` (React 19 shape)
     * is not accepted by React 18's `LegacyRef`.
     */
    mapContainerRef: React.MutableRefObject<HTMLDivElement | null>;
    mapErrorType: MapErrorType | null;
    fitBoundsToMarkers: (options?: FitBoundsToMarkersOptions) => void;
    registerFitBoundsToMarkers: (
        fn: (options?: FitBoundsToMarkersOptions) => void
    ) => void;
}

export const MapContext = React.createContext<MapContextValue | null>(null);

/**
 * Consume the surrounding `MapProvider`. Throws if called outside one so
 * misuse fails loudly at render time.
 */
export function useMap(): MapContextValue {
    const value = React.useContext(MapContext);
    if (!value) {
        throw new Error('useMap must be used inside a <MapProvider>');
    }
    return value;
}

// cSpell:ignore styledata
